import moment from "moment";
import { MatchTalentIcon } from "../../constants/svg";
import { currencyFormat, getProgressPercent } from "../../utils/functions";
import { useNavigate } from "react-router";
import {
  IconBriefcase,
  IconClock,
  IconWorld,
  IconBuilding,
  IconLayoutDistributeVertical,
  IconMapPin,
  IconCalendar,
  IconCircleCheck,
  IconCircleX,
  IconUsers,
  IconEye,
  IconEdit,
  IconTrash,
  IconSend,
  IconRefresh,
  IconCheck,
  IconX,
  IconStarFilled,
  IconStarHalfFilled,
  IconStar,
  IconLoader,
} from "@tabler/icons-react";
import styles from "./JobCard.module.css";
import clsx from "clsx";

// Maps job.modeIcon -> the icon component shown inside the mode chip
const MODE_ICONS = {
  world: IconWorld,
  building: IconBuilding,
  hybrid: IconLayoutDistributeVertical,
  relocation: IconMapPin,
};

const STATUS_META = {
  active: {
    label: "Active",
    icon: IconCircleCheck,
    className: styles.statusActive,
  },
  "in-progress": {
    label: "Active",
    icon: IconCircleCheck,
    className: styles.statusActive,
  },
  "in-review": {
    label: "In Review",
    icon: IconLoader,
    className: styles.statusReview,
  },
  draft: { label: "Draft", icon: null, className: styles.statusDraft },
  archived: { label: "Archived", icon: null, className: styles.statusArchived },
  completed: {
    label: "Completed",
    icon: IconCircleCheck,
    className: styles.statusCompleted,
  },
  closed: {
    label: "Closed",
    icon: IconCircleX,
    className: styles.statusClosed,
  },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span className={`${styles.statusChip} ${meta.className}`}>
      {Icon && <Icon size={12} />} {meta.label}
    </span>
  );
}

function MatchChip({ value }) {
  const cls = matchClass(value);
  const Icon =
    cls === "high"
      ? IconStarFilled
      : cls === "mid"
        ? IconStarHalfFilled
        : IconStar;
  const clsMap = {
    high: styles.matchHigh,
    mid: styles.matchMid,
    low: styles.matchLow,
  };
  return (
    <span className={`${styles.matchChip} ${clsMap[cls]}`}>
      <Icon size={12} /> {value}% match
    </span>
  );
}

function matchClass(n) {
  return n >= 80 ? "high" : n >= 60 ? "mid" : "low";
}

export const statusClasses = {
  draft: "bg-[#f1f5f9] text-[#64748b]",
  "in-review": "bg-[#fef3c7] text-[#92400e]",
  "in-progress": "bg-[#eef2ff] text-[#1d4ed8]",
  archived: "bg-[#ffe4e6] text-[#991b1b]",
  completed: "bg-[#fff7ed] text-[#7c2d12]",
  closed: "bg-[#fefce8] text-[#713f12]",
};

const JobCard = ({
  setApplicantID,
  setCancelModal,
  setMarkJobModal,
  setMarkType,
  tab,
  deleteJob,
  handleMatchRandomTalent,
  talentId,

  job,
  onOpenDetails,
  onOpenApplicants,
  onRestore,
  setSelectedJob,
  setJobDetailsModalOpen,
}) => {
  const navigate = useNavigate();

  const ModeIcon = MODE_ICONS[job?.modeIcon] || IconWorld;

  // Title-line badge: match chip on the Active tab, status chip everywhere except
  // the In-Review tab (which shows its status chip in the actions slot instead).
  let titleBadge = null;
  const jobStatus = job?.jobStatus?.toLowerCase();
  const status = job?.status?.toLowerCase() === "draft" ? "draft" : jobStatus;

  if (tab === "active")
    titleBadge = (
      <MatchChip
        value={getProgressPercent(job?.startDate, job?.endDate) ?? 0}
      />
    );
  else if (tab !== "in-review") titleBadge = <StatusChip status={status} />;

  const stopAndRun = (fn) => (e) => {
    e.stopPropagation();
    fn && fn(job);
  };

  return (
    <div
      className={styles.card}
      // onClick={() => navigate(`${job._id}`, { state: job })}
    >
      <div
        className={styles.jobRow}
        onClick={() => onOpenDetails && onOpenDetails(job)}
      >
        <div className={clsx(styles.jobAvatar, statusClasses[status])}>
          <IconBriefcase size={22} />
        </div>

        <div className={styles.jobMain}>
          <div className={styles.jobTitle}>
            {job?.title} {titleBadge}
          </div>
          <div className={styles.jobMeta}>
            <span className={styles.modeChip}>
              <IconClock size={12} /> {job?.type}
            </span>
            <span className={styles.modeChip}>
              <ModeIcon size={12} /> {job?.workMode}
            </span>
            <span className={styles.modeChip}>{job?.duration}</span>
            <span className={styles.jobMetaDot} />
            <span className={styles.jobDateText}>
              <IconCalendar size={13} /> {job?.posted}{" "}
              {moment(job?.endDate).format("DD-MM-YYYY")}
            </span>
          </div>
        </div>

        <div className={styles.jobRight}>
          <div className={styles.jobPay}>
            <div className={styles.jobPayLabel}>Salary</div>
            <div className={styles.jobPayVal}>
              {" "}
              &#8358;{currencyFormat(Number(job?.budget ?? 0))}
            </div>
            <div className={styles.jobPaySub}>
              {job?.applicantTalentIds?.length ?? 0} applicants
            </div>
          </div>

          <div className={styles.jobActions}>
            {tab === "all" && talentId && (
              <span
                className={`${styles.jobEdit} cursor-pointer`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMatchRandomTalent(job._id);
                }}
              >
                <MatchTalentIcon />
              </span>
            )}
            {tab === "active" && (
              <>
                {job?.jobStatus?.toLowerCase() === "in-review" ? (
                  <StatusChip status={status} />
                ) : (
                  <>
                    <button
                      className={`${styles.jab} ${styles.jabApprove}`}
                      title="Approve"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedJob(job);
                        setMarkJobModal(true);
                        setApplicantID(job?._id);
                        setMarkType("complete");
                      }}
                    >
                      <IconCheck size={18} />
                    </button>
                    <button
                      className={`${styles.jab} ${styles.jabReject}`}
                      title="Close job"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedJob(job);
                        setCancelModal(true);
                        setApplicantID(job?._id);
                        setMarkType("cancel");
                      }}
                    >
                      <IconX size={18} />
                    </button>
                  </>
                )}
              </>
            )}

            {tab?.toLowerCase() === "in-review" && (
              <StatusChip status={job?.jobStatus} />
            )}

            {tab === "drafts" && (
              <>
                <button
                  className={`${styles.jab} ${styles.jabEdit}`}
                  title="Edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/add-new-job/${job?.jobType?.toLowerCase()}?edit=true&id=${
                        job?._id
                      }`,
                    );
                  }}
                >
                  <IconEdit size={18} />
                </button>
                <button
                  className={`${styles.jab} ${styles.jabDelete}`}
                  title="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteJob(job?._id);
                  }}
                >
                  <IconTrash size={18} />
                </button>
                <button
                  className={`${styles.jab} ${styles.jabPublish}`}
                  title="Publish"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/add-new-job/${job?.jobType?.toLowerCase()}?edit=true&id=${
                        job?._id
                      }`,
                    );
                  }}
                >
                  <IconSend size={18} />
                </button>
              </>
            )}

            {tab === "archived" && (
              <>
                <button
                  className={styles.jab}
                  title="Restore"
                  onClick={stopAndRun(onRestore)}
                >
                  <IconRefresh size={18} />
                </button>
                <button
                  className={styles.jab}
                  title="View details"
                  onClick={stopAndRun(onOpenDetails)}
                >
                  <IconEye size={18} />
                </button>
              </>
            )}

            {(tab === "all" || tab === "completed" || tab === "cancelled") && (
              <>
                {tab !== "cancelled" && (
                  <button
                    className={styles.jab}
                    title="View applicants"
                    onClick={stopAndRun(onOpenApplicants)}
                  >
                    <IconUsers size={18} />
                  </button>
                )}
                <button
                  className={styles.jab}
                  title="View details"
                  onClick={stopAndRun(onOpenDetails)}
                >
                  <IconEye size={18} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCard;
